﻿import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Material } from '@babylonjs/core/Materials/material';
import { Color3, Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import '@babylonjs/core/Rendering/boundingBoxRenderer'; // Required by mesh bounding box feature
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import { UvMapFeature } from '@models/classes';
import { IVertexGroup } from '@models/classes/kb3d-models';
import { LightNode } from '@models/classes/lights/light-node';
import { MeshNode, SpaceNode } from '@models/classes/meshes';
import { KbQuaternion } from '@models/classes/primitives/kb-quaternion';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { ViewpointNode } from '@models/classes/viewpoints';
import { KbViewer } from '@view/kb-viewer';
import { Subject } from 'rxjs';
import { KbAxisAngleGizmo } from './gizmos/axis-angle-gizmo';
import { KbFacetSelectGizmo } from './gizmos/facet-select-gizmo';
import { KbGizmo } from './gizmos/gizmo';
import { KbLightGizmo } from './gizmos/light-gizmo';
import { KbPositionGizmo } from './gizmos/position-gizmo';
import { KbRotationGizmo } from './gizmos/rotation-gizmo';
import { KbScaleGizmo } from './gizmos/scale-gizmo';
import { KbVertexSelectGizmo } from './gizmos/vertex-select-gizmo';
import { KbViewpointGizmo } from './gizmos/viewpoint-gizmo';

interface GizmoInfo {
    subjects: Array<Subject<any>>;
    gizmo: KbGizmo;
}

/**
 * Manages the various gizmos and their connections to the scene meshes.
 * Keeps track of what gizmo is active and the selected meshes to put the gizmos on.
 * @param scene The root scene on which the utility/gizmo layer will be placed.
 * @param camera The camera being used. Required by some gizmos for displaying them correctly
 */
const PREVIEW_NAME = '__UV_PREVIEW_MATERIAL__';
export class GizmoTools {
    activeGizmoSettings: GizmoInfo[] = [];
    dragging = false;
    uvMapStoredMaterials = new Map<string, Material>();
    private scene: Scene;
    private gizmoLayer: UtilityLayerRenderer;
    private previewMaterial: PBRMaterial | undefined;

    constructor(scene: Scene, private kbViewer: KbViewer) {
        this.scene = scene;
        this.gizmoLayer = kbViewer.utilityLayer;
        this.gizmoLayer.utilityLayerScene.useRightHandedSystem = true;
    }

    selectLight(lightNode: LightNode) {
        const lightGizmo = new KbLightGizmo(this.kbViewer, this.gizmoLayer);

        const updatePosition = new Subject<KbVector>();
        const updateDirection = new Subject<KbVector>();
        const totalDeltaPosition = new Subject<KbVector>();
        this.activeGizmoSettings.push({
            subjects: [updatePosition, updateDirection, totalDeltaPosition],
            gizmo: lightGizmo,
        });

        lightGizmo.attachTo(lightNode);
        this.dragging = false;

        return {
            updateLightGizmo: (node: LightNode) => {
                lightGizmo.updateGizmoPosition(node);
            },
        };
    }

    selectViewpoint(viewpoint: ViewpointNode) {
        const viewpointGizmo = new KbViewpointGizmo(this.kbViewer, this.gizmoLayer);
        viewpointGizmo.attachTo(viewpoint);

        const updatePosition = new Subject<KbVector>();
        const updateTargetPosition = new Subject<KbVector>();
        const updateRotation = new Subject<KbVector>();
        const totalDeltaPosition = new Subject<KbVector>();
        const totalDeltaTargetPosition = new Subject<KbVector>();
        const totalDeltaRotation = new Subject<KbVector>();
        this.activeGizmoSettings.push({
            subjects: [
                updatePosition,
                updateTargetPosition,
                updateRotation,
                totalDeltaPosition,
                totalDeltaTargetPosition,
                totalDeltaRotation,
            ],
            gizmo: viewpointGizmo,
        });

        this.dragging = false;
        const totalDeltaPositionQueue = Vector3.Zero();
        const totalDeltaTargetPositionQueue = Vector3.Zero();
        const totalDeltaRotationQueue = Quaternion.Identity();

        viewpointGizmo.onDragStartObservable.add(() => {
            this.dragging = true;
        });
        viewpointGizmo.onDragObservable.add(event => {
            if (this.dragging) {
                if (event.deltaPos) {
                    updatePosition.next(KbVector.FromVec3(event.deltaPos));
                    totalDeltaPositionQueue.addInPlace(event.deltaPos);
                }
                if (event.deltaTargetPos) {
                    updateTargetPosition.next(KbVector.FromVec3(event.deltaTargetPos));
                    totalDeltaTargetPositionQueue.addInPlace(event.deltaTargetPos);
                }
                if (event.deltaRotation) {
                    updateRotation.next(KbVector.FromVec3(event.deltaRotation.toEulerAngles().scale(180 / Math.PI)));
                    totalDeltaRotationQueue.multiplyInPlace(event.deltaRotation);
                }
            }
        });
        viewpointGizmo.onDragEndObservable.add(() => {
            if (this.dragging) {
                this.dragging = false;
                if (!totalDeltaPositionQueue.equalsToFloats(0, 0, 0)) {
                    totalDeltaPosition.next(KbVector.FromVec3(totalDeltaPositionQueue));
                    totalDeltaPositionQueue.setAll(0);
                } else if (!totalDeltaTargetPositionQueue.equalsToFloats(0, 0, 0)) {
                    totalDeltaTargetPosition.next(KbVector.FromVec3(totalDeltaTargetPositionQueue));
                    totalDeltaTargetPositionQueue.setAll(0);
                } else if (!totalDeltaRotationQueue.equals(Quaternion.Identity())) {
                    totalDeltaRotation.next(
                        KbVector.FromVec3(totalDeltaRotationQueue.toEulerAngles().scale(180 / Math.PI))
                    );
                    totalDeltaRotationQueue.copyFrom(Quaternion.Identity());
                }
            }
        });

        return {
            updatePosition: updatePosition.asObservable(),
            updateTargetPosition: updateTargetPosition.asObservable(),
            updateRotation: updateRotation.asObservable(),
            totalDeltaPosition: totalDeltaPosition.asObservable(),
            totalDeltaTargetPosition: totalDeltaTargetPosition.asObservable(),
            totalDeltaRotation: totalDeltaRotation.asObservable(),
            updateViewpointGizmo: (node: ViewpointNode) => {
                viewpointGizmo.updateGizmoPosition(node);
            },
        };
    }

    position(
        target: SpaceNode | KbVector,
        positionSpace?: SpaceNode,
        axisVisibility?: [boolean, boolean, boolean],
        orientGizmo?: Quaternion
    ) {
        const positionGizmo = new KbPositionGizmo(this.kbViewer, this.gizmoLayer, axisVisibility);
        positionGizmo.attachTo(target, positionSpace, orientGizmo);

        const updatePosition = new Subject<KbVector>();
        const totalDelta = new Subject<KbVector>();
        this.activeGizmoSettings.push({
            subjects: [updatePosition, totalDelta],
            gizmo: positionGizmo,
        });
        this.dragging = false;
        let totalDeltaQueue = Vector3.Zero();

        positionGizmo.onDragStartObservable.add(() => {
            this.dragging = true;
        });
        positionGizmo.onDragObservable.add(event => {
            if (this.dragging) {
                totalDeltaQueue.addInPlace(event.delta);
                updatePosition.next(KbVector.FromVec3(event.delta));
            }
        });
        positionGizmo.onDragEndObservable.add(() => {
            if (this.dragging) {
                totalDelta.next(KbVector.FromVec3(totalDeltaQueue));
                totalDeltaQueue = Vector3.Zero();
                this.dragging = false;
            }
        });

        return {
            update: updatePosition.asObservable(),
            totalDelta: totalDelta.asObservable(),
            updateGizmoPosition: (update?: KbVector) => positionGizmo.updateGizmoPosition(update),
            dispose: () => this.clearGizmo(positionGizmo),
        };
    }

    rotateAxisAngle(
        target: KbVector,
        positionSpace?: SpaceNode,
        options?: { placementOffset?: KbVector; disableAngle?: boolean }
    ) {
        const updateAxisAngle = new Subject<{ axis: KbVector; angle: number }>();
        const totalDelta = new Subject<{ axis: KbVector; angle: number }>();
        const totalDeltaQueue = { axis: Vector3.Zero(), angle: 0 };

        const axisAngleGizmo = new KbAxisAngleGizmo(this.kbViewer, this.gizmoLayer, options?.disableAngle);
        axisAngleGizmo.attachTo(target, positionSpace, options?.placementOffset);

        this.activeGizmoSettings.push({
            subjects: [updateAxisAngle, totalDelta],
            gizmo: axisAngleGizmo,
        });
        this.dragging = false;

        axisAngleGizmo.onDragStartObservable.add(() => {
            this.dragging = true;
        });
        axisAngleGizmo.onDragObservable.add(event => {
            if (this.dragging) {
                const angle = (event.deltaAngle * 180) / Math.PI;
                totalDeltaQueue.axis.addInPlace(event.deltaAxis);
                totalDeltaQueue.angle += angle;
                updateAxisAngle.next({ axis: KbVector.FromVec3(event.deltaAxis), angle });
            }
        });
        axisAngleGizmo.onDragEndObservable.add(() => {
            if (this.dragging) {
                totalDelta.next({ axis: KbVector.FromVec3(totalDeltaQueue.axis), angle: totalDeltaQueue.angle });
                totalDeltaQueue.axis = Vector3.Zero();
                totalDeltaQueue.angle = 0;
                this.dragging = false;
            }
        });

        return {
            update: updateAxisAngle.asObservable(),
            totalDelta: totalDelta.asObservable(),
            updateGizmoPosition: (updateAxis?: KbVector, updatePosition?: KbVector) =>
                axisAngleGizmo.updateGizmoPosition(updateAxis, updatePosition),
            dispose: () => this.clearGizmo(axisAngleGizmo),
        };
    }

    /**
     * Creates a rotation gizmo that can rotate many items
     * nodes will be rotated and repositioned in space if the center of rotation is not equal to the position
     * vectors will be repositioned in space as if each vector is the position of a point
     */
    rotate(
        targets: Array<SpaceNode | KbVector>,
        positionSpace?: SpaceNode,
        placementOffset?: KbVector,
        axisVisibility?: [boolean, boolean, boolean]
    ) {
        let gizmoOffset = Vector3.Zero();

        // Get the transform matrix to get the world space rotation and coordinates of the node we are rotating
        let positionTransform: Matrix | undefined;
        if (positionSpace) {
            positionTransform = this.kbViewer.getRendererNode(positionSpace)?.computeWorldMatrix(true);
        } else if (targets[0] instanceof SpaceNode && targets[0]._parent) {
            positionTransform = this.kbViewer.getRendererNode(targets[0]._parent)?.computeWorldMatrix(true);
        }
        const rotationGizmo = new KbRotationGizmo(this.kbViewer, this.gizmoLayer, undefined, axisVisibility);

        if (targets.length === 1) {
            const target = targets[0];
            rotationGizmo.attachTo(target, positionSpace, placementOffset);
        } else {
            // If multiple targets are selected, put the pivot in the average of the positions
            // Maybe enhance this to use bounding box later, if that's important...
            const pivot = targets
                .reduce((result: KbVector, current) => {
                    return result.add(current instanceof SpaceNode ? current.position : current);
                }, KbVector.Zero())
                .scale(1 / targets.length);
            rotationGizmo.attachTo(KbVector.Zero(), undefined, pivot);
        }

        const update = new Subject<
            Array<{ rotation: KbVector; position: KbVector; axisTranslate: KbVector; worldTransformMatrix: Matrix }>
        >();
        const totalDelta = new Subject<{
            rotation: KbVector;
            position: KbVector;
            axisTranslate: KbVector;
        }>();
        this.activeGizmoSettings.push({
            subjects: [update, totalDelta],
            gizmo: rotationGizmo,
        });

        this.dragging = false;
        let totalDeltaQueue = {
            rotation: Quaternion.Identity(),
            position: Vector3.Zero(),
            axisTranslate: Vector3.Zero(),
        };

        // Set up caches for the positions and rotations of the nodes because it's not guaranteed that they will be updated by reference
        // And as you drag the nodes these values will change and the gizmos won't be dragging in the right direction anymore
        const positions = targets.map(target =>
            target instanceof SpaceNode ? target.position.toVec3() : target.toVec3()
        );
        const axes = targets.map(target => (target instanceof SpaceNode ? target.rotationAxis.toVec3() : null));
        const quaternionCache = targets.map(target =>
            target instanceof SpaceNode
                ? Quaternion.FromEulerVector(target.eulerRotation.toVec3().scale(Math.PI / 180))
                : Quaternion.Identity()
        );

        // Get the current pivot which will always be where the gizmo is located (minus the offset which just visually offsets the gizmo position)
        let gizmoPosition = rotationGizmo.attachedMesh!.position;

        rotationGizmo.onDragStartObservable.add(() => {
            this.dragging = true;
            if (rotationGizmo.gizmoOffset) {
                gizmoOffset = rotationGizmo.gizmoOffset.toVec3();
            }
            if (gizmoOffset) {
                gizmoPosition = gizmoPosition.subtract(gizmoOffset);
            }
        });
        rotationGizmo.onDragObservable.add(event => {
            if (this.dragging) {
                const result: Array<{
                    rotation: KbVector;
                    position: KbVector;
                    axisTranslate: KbVector;
                    worldTransformMatrix: Matrix;
                }> = [];

                const rotationMatrix = new Matrix();

                const gizmoRotation = rotationGizmo.attachedMesh!.rotationQuaternion || Quaternion.Identity();
                // Get the true world space rotation in the case that the gizmo is rotated
                // The gizmos output their rotation deltas in local space
                const meshSpaceRotation = gizmoRotation.multiply(event.delta).multiply(gizmoRotation.conjugate());
                meshSpaceRotation.toRotationMatrix(rotationMatrix);
                const worldTransformMatrix = new Matrix();
                event.delta.toRotationMatrix(worldTransformMatrix);
                worldTransformMatrix.multiplyToRef(
                    rotationGizmo.attachedMesh!.computeWorldMatrix(),
                    worldTransformMatrix
                );

                // Inverse the position transform to rotate the delta back into local "positionSpace" space
                const positionTransformInv = positionTransform?.clone().invert();

                positions.forEach((position, index) => {
                    let positionDelta: Vector3;
                    let axisDelta: Vector3 | undefined;
                    const target = targets[index];
                    const axis = axes[index];

                    // If the node is a space node, then rotate it around the pivot to get the updated position
                    if (target instanceof SpaceNode) {
                        // Treat the pivot point as the center of the node so the node rotates around the pivot point
                        let worldNodePivot = target.pivot.toVec3();
                        const nodeTransform = this.kbViewer.getRendererNode(target)?.computeWorldMatrix(true);
                        if (nodeTransform) {
                            worldNodePivot = Vector3.TransformCoordinates(worldNodePivot, nodeTransform);
                        }
                        positionDelta = Vector3.TransformCoordinates(
                            worldNodePivot.subtract(gizmoPosition),
                            rotationMatrix
                        )
                            .add(gizmoPosition)
                            .subtract(worldNodePivot);
                        // If the node is a vector, then it's a direction vector, so we want to rotate it as if the vector started from the origin.
                        // So don't rotate around the pivot point. The updated position will actually be the updated direction.
                    } else {
                        positionDelta = Vector3.TransformCoordinates(position, rotationMatrix).subtract(position);
                    }

                    if (axis) {
                        axisDelta = Vector3.TransformCoordinates(
                            axis.length() === 0 ? new Vector3(0, 1, 0) : axis,
                            rotationMatrix
                        ).subtract(axis);
                        axis.addInPlace(axisDelta);
                    }

                    // Rotate the delta vector into local "positionSpace" space
                    if (positionTransformInv) {
                        positionDelta = Vector3.TransformCoordinates(
                            positionDelta,
                            positionTransformInv.getRotationMatrix()
                        );
                    }

                    // Update the position cache by reference (make sure this position variable isn't shadowed)
                    position.addInPlace(positionDelta);
                    totalDeltaQueue.position.addInPlace(positionDelta);
                    if (axisDelta) {
                        totalDeltaQueue.axisTranslate.addInPlace(axisDelta);
                    }

                    // Get the rotation delta by converting the previous quaternion rotation to euler, rotating to the new position, and getting euler angles of that.
                    // This is necessary because it we output the plain quaternion delta euler, the euler rotation order could result
                    // In an incorrect rotation if there are other euler angles being rotated first
                    const previousAngle = quaternionCache[index].toEulerAngles();
                    quaternionCache[index].multiplyInPlace(event.delta);
                    const newAngle = quaternionCache[index].toEulerAngles();

                    totalDeltaQueue.rotation.multiplyInPlace(event.delta);
                    result.push({
                        position: KbVector.FromVec3(positionDelta),
                        rotation: KbVector.FromVec3(newAngle.subtract(previousAngle).scale(180 / Math.PI)),
                        worldTransformMatrix: worldTransformMatrix,
                        axisTranslate: axisDelta ? KbVector.FromVec3(axisDelta) : KbVector.Zero(),
                    });
                });
                update.next(result);
            }
        });
        rotationGizmo.onDragEndObservable.add(() => {
            if (this.dragging) {
                totalDelta.next({
                    rotation: KbVector.FromVec3(totalDeltaQueue.rotation.toEulerAngles().scale(180 / Math.PI)),
                    position: KbVector.FromVec3(totalDeltaQueue.position),
                    axisTranslate: KbVector.FromVec3(totalDeltaQueue.axisTranslate),
                });
                totalDeltaQueue = {
                    rotation: Quaternion.Identity(),
                    position: Vector3.Zero(),
                    axisTranslate: Vector3.Zero(),
                };
                this.dragging = false;
            }
        });

        return {
            update: update.asObservable(),
            totalDelta: totalDelta.asObservable(),
            updateGizmoPosition: (update?: KbVector, offset?: KbVector) =>
                rotationGizmo.updateGizmoPosition(update, offset),
            dispose: () => this.clearGizmo(rotationGizmo),
        };
    }

    scale(targets: Array<SpaceNode | KbVector>, positionSpace?: SpaceNode) {
        // if (targets.length === 0) {
        //     return {
        //         update: empty(),
        //         totalDelta: empty(),
        //     };
        // }

        const scaleGizmo = new KbScaleGizmo(this.kbViewer, this.gizmoLayer);

        if (targets.length === 1) {
            const target = targets[0];
            scaleGizmo.attachTo(target, positionSpace);
        } else {
            // If multiple targets are selected, put the pivot in the average of the positions
            // Maybe enhance this to use bounding box later, if that's important...
            const pivot = targets
                .reduce((result: KbVector, current) => {
                    return result.add(current instanceof SpaceNode ? current.position : current);
                }, KbVector.Zero())
                .scale(1 / targets.length);

            scaleGizmo.attachTo(pivot, positionSpace);
        }

        const update = new Subject<Array<{ scaling: KbVector; position: KbVector }>>();
        const totalDelta = new Subject<KbVector>();
        this.activeGizmoSettings.push({
            subjects: [update, totalDelta],
            gizmo: scaleGizmo,
        });

        this.dragging = false;
        let totalDeltaQueue = Vector3.Zero();
        let positionTransform: Matrix | undefined;

        // Get the transform matrix to get the world space rotation and coordinates of the node we are scaling
        if (positionSpace) {
            positionTransform = this.kbViewer.getRendererNode(positionSpace)?.computeWorldMatrix(true);
        } else if (targets[0] instanceof SpaceNode && targets[0]._parent) {
            positionTransform = this.kbViewer.getRendererNode(targets[0]._parent)?.computeWorldMatrix(true);
        }

        // Get the current pivot which will always be where the gizmo is located
        let gizmoPosition = scaleGizmo.attachedMesh!.position;

        scaleGizmo.onDragStartObservable.add(() => {
            this.dragging = true;
            if (scaleGizmo.gizmoOffset) {
                gizmoPosition = gizmoPosition.subtract(scaleGizmo.gizmoOffset.toVec3());
            }
        });
        scaleGizmo.onDragObservable.add(event => {
            if (this.dragging) {
                const result: Array<{ scaling: KbVector; position: KbVector }> = [];

                targets.forEach(target => {
                    let positionDelta: Vector3;

                    // If the node is a space node, then rotate it around the pivot to get the updated position
                    if (target instanceof SpaceNode) {
                        // Treat the pivot point as the center of the node so the node rotates around the pivot point
                        let worldNodePivot = target.pivot.toVec3();
                        const nodeTransform = this.kbViewer.getRendererNode(target)?.computeWorldMatrix(true);
                        if (nodeTransform) {
                            worldNodePivot = Vector3.TransformCoordinates(worldNodePivot, nodeTransform);
                        }
                        positionDelta = worldNodePivot
                            .subtract(gizmoPosition)
                            .multiply(event.delta)
                            .add(gizmoPosition)
                            .subtract(worldNodePivot);
                        // If the node is a vector, then it's a direction vector, so we want to rotate it as if the vector started from the origin.
                        // So don't rotate around the pivot point. The updated position will actually be the updated direction.
                    } else {
                        // Transform the node position to world space because the pivot and gizmo will be in world space as well
                        let worldPosition = target.toVec3();
                        if (positionTransform) {
                            worldPosition = Vector3.TransformCoordinates(worldPosition, positionTransform);
                        }
                        positionDelta = worldPosition.multiply(event.delta).subtract(worldPosition);
                    }

                    totalDeltaQueue.addInPlace(event.delta);
                    result.push({
                        position: KbVector.FromVec3(positionDelta),
                        scaling: KbVector.FromVec3(event.delta),
                    });
                });
                update.next(result);
            }
        });
        scaleGizmo.onDragEndObservable.add(() => {
            if (this.dragging) {
                totalDelta.next(KbVector.FromVec3(totalDeltaQueue));
                totalDeltaQueue = Vector3.Zero();
                this.dragging = false;
            }
        });

        return {
            update: update.asObservable(),
            totalDelta: totalDelta.asObservable(),
            updateGizmoPosition: (update?: KbVector) => scaleGizmo.updateGizmoPosition(update),
            dispose: () => this.clearGizmo(scaleGizmo),
        };
    }

    selectVertices(target: MeshNode, selectedVertices?: number[]) {
        const vertexSelectionGizmo = new KbVertexSelectGizmo(this.kbViewer, this.scene);
        vertexSelectionGizmo.attachTo(target);

        const update = new Subject<{
            vertexIndices: number[];
        }>();
        this.activeGizmoSettings.push({
            subjects: [update],
            gizmo: vertexSelectionGizmo,
        });

        if (selectedVertices) {
            vertexSelectionGizmo.selectVertices(selectedVertices);
        }

        this.dragging = false;
        vertexSelectionGizmo.onSelectionChange.add(result => {
            if (result) {
                update.next({
                    vertexIndices: result.selectedIndices,
                });
            }
        });

        return {
            update: update.asObservable(),
            vertexTracker: vertexSelectionGizmo.vertexTracker,
            updateGizmoPositions: () => vertexSelectionGizmo.updateGizmoPosition(),
            updateParticleScale: (scale: number) => vertexSelectionGizmo.updateParticleScale(scale),
            disableSelectionChange: () => vertexSelectionGizmo.disableSelectionChange(),
            enableSelectionChange: () => vertexSelectionGizmo.enableSelectionChange(),
            getBarycenter: (indices: number[]) => vertexSelectionGizmo.getBarycenter(indices),
            dispose: () => this.clearGizmo(vertexSelectionGizmo),
        };
    }

    selectFacets(target: MeshNode, selectedVertices?: IVertexGroup[]) {
        const facetSelectionGizmo = new KbFacetSelectGizmo(this.kbViewer, this.scene);
        facetSelectionGizmo.attachTo(target);

        const update = new Subject<{
            selectedFacets: Array<Required<IVertexGroup>>;
        }>();
        this.activeGizmoSettings.push({
            subjects: [update],
            gizmo: facetSelectionGizmo,
        });

        if (selectedVertices) {
            facetSelectionGizmo.selectFacetGroups(selectedVertices);
        }

        this.dragging = false;
        facetSelectionGizmo.onSelectionChange.add(result => {
            if (result) {
                update.next({
                    selectedFacets: result.selectedFacets,
                });
            }
        });

        return {
            update: update.asObservable(),
            updateGizmoPositions: () => facetSelectionGizmo.updateGizmoPosition(),
            disableSelectionChange: () => facetSelectionGizmo.disableSelectionChange(),
            enableSelectionChange: () => facetSelectionGizmo.enableSelectionChange(),
            dispose: () => this.clearGizmo(facetSelectionGizmo),

            setBackfaceCulling: (enabled: boolean) => facetSelectionGizmo.setBackfaceCulling(enabled),
            setTolerance: (tolerance: number) => facetSelectionGizmo.setTolerance(tolerance),
            setVisualization: (opacity: number) => facetSelectionGizmo.setVisualization(opacity),
            expandSelection: (tolerance?: number) => facetSelectionGizmo.expandSelection(tolerance),
        };
    }

    clearGizmo(gizmo?: KbGizmo) {
        if (gizmo) {
            const index = this.activeGizmoSettings.findIndex(g => g.gizmo === gizmo);
            if (index >= 0) {
                const gizmoSettings = this.activeGizmoSettings[index];
                gizmoSettings.subjects.forEach(subject => subject.complete());
                gizmoSettings.gizmo.dispose();

                this.activeGizmoSettings.splice(index, 1);
            }
        } else {
            this.activeGizmoSettings.forEach(gizmoSettings => {
                gizmoSettings.subjects.forEach(subject => subject.complete());
                gizmoSettings.gizmo.dispose();
            });
            this.activeGizmoSettings = [];
        }
    }

    updateGizmoPositions() {
        if (!this.dragging) {
            this.activeGizmoSettings.forEach(gizmoSettings => {
                gizmoSettings.gizmo.updateGizmoPosition();
            });
        }
    }

    showUvMapMaterial(target: UvMapFeature) {
        if (target._showMaterial && target._previewMaterialVisible) {
            if (target._parent) {
                const mesh = this.kbViewer.getRendererNode(target._parent);
                if (mesh instanceof Mesh) {
                    if (mesh.material != null && mesh.material.name != PREVIEW_NAME) {
                        const previousMaterial = mesh.material;
                        this.uvMapStoredMaterials.set(target.id, previousMaterial);
                    }

                    if (!this.previewMaterial) {
                        this.previewMaterial = new PBRMaterial(PREVIEW_NAME, this.kbViewer.scene);
                        const previewTexture = new Texture('/assets/images/uv_colorgrid.png', this.kbViewer.scene);
                        this.previewMaterial.albedoTexture = previewTexture;
                        this.previewMaterial.albedoColor = Color3.White();
                        this.previewMaterial.metallic = 0;
                        this.previewMaterial.roughness = 1;
                    }

                    mesh.material = this.previewMaterial;
                }
            }
        } else {
            if (target._parent) {
                const mesh = this.kbViewer.getRendererNode(target._parent);
                if (mesh instanceof Mesh) {
                    const storedMaterial = this.uvMapStoredMaterials.get(target.id);
                    if (storedMaterial) {
                        mesh.material = storedMaterial;
                    }
                }
            }
        }
    }

    // Bounding mesh code is not used right now, but it may be useful when we are doing mates
    // private calculateBoundingBox(nodes: TransformNode[]) {
    //     const meshes = nodes.filter<Mesh>((node): node is Mesh => node instanceof Mesh);
    //     meshes.forEach(mesh => mesh.computeWorldMatrix(true));
    //     if (meshes.length === 0) {
    //         return {
    //             pivot: Vector3.Zero(),
    //             boundingInfo: new BoundingInfo(Vector3.Zero(), Vector3.Zero())
    //         };
    //     }

    //     let boundingInfo = meshes[0].getBoundingInfo();
    //     let minimumWorld = boundingInfo.boundingBox.minimumWorld;
    //     let maximumWorld = boundingInfo.boundingBox.maximumWorld;

    //     for (let i = 1; i < meshes.length; i++) {
    //         let boundingInfo = meshes[i].getBoundingInfo();
    //         minimumWorld = Vector3.Minimize(minimumWorld, boundingInfo.boundingBox.minimumWorld);
    //         maximumWorld = Vector3.Maximize(maximumWorld, boundingInfo.boundingBox.maximumWorld);
    //     }
    //     let bounding = new BoundingInfo(minimumWorld, maximumWorld);
    //     let middlePivot = new Vector3(bounding.boundingBox.center.x, bounding.boundingBox.center.y, bounding.boundingBox.center.z);

    //     return {
    //         pivot: middlePivot,
    //         boundingInfo: bounding
    //     };
    // }

    // private displayKbBoundingBox(mesh: Mesh) {
    //     let oMesh: Mesh & { kbBoundingBox?: boundingBoxGeometry };
    //     oMesh = mesh;
    //     var bbNode: boundingBoxGeometry;

    //     if (this.currentSelectedMeshes.length === 0) {
    //         if (oMesh.kbBoundingBox) {
    //             oMesh.kbBoundingBox.wireFrame.dispose();
    //             delete oMesh.kbBoundingBox;
    //         }
    //         return;
    //     }

    //     // if the mesh is resized/rotated/something we need to recalculate the bounding box
    //     mesh.computeWorldMatrix(true);
    //     mesh.refreshBoundingInfo();

    //     mesh.refreshBoundingInfo = (applySkeleton?: boolean) => {
    //         return mesh;
    //     };

    //     const center = mesh.getBoundingInfo().boundingBox.centerWorld.clone();
    //     const diagonalLen = mesh.getBoundingInfo().diagonalLength;

    //     const min = mesh.getBoundingInfo().boundingBox.minimumWorld;
    //     const max = mesh.getBoundingInfo().boundingBox.maximumWorld;
    //     const d = max.subtract(min);

    //     const bOffset = 0.02;
    //     d.x += bOffset;
    //     d.y += bOffset;
    //     d.z += bOffset;
    //     // calculate the position of the bounding box vertices
    //     const v1 = new Vector3(center.x - d.x / 2, center.y + d.y / 2, center.z + d.z / 2);
    //     const v2 = new Vector3(center.x + d.x / 2, center.y + d.y / 2, center.z + d.z / 2);
    //     const v3 = new Vector3(center.x - d.x / 2, center.y - d.y / 2, center.z + d.z / 2);
    //     const v4 = new Vector3(center.x + d.x / 2, center.y - d.y / 2, center.z + d.z / 2);
    //     const v5 = new Vector3(center.x - d.x / 2, center.y + d.y / 2, center.z - d.z / 2);
    //     const v6 = new Vector3(center.x + d.x / 2, center.y + d.y / 2, center.z - d.z / 2);
    //     const v7 = new Vector3(center.x - d.x / 2, center.y - d.y / 2, center.z - d.z / 2);
    //     const v8 = new Vector3(center.x + d.x / 2, center.y - d.y / 2, center.z - d.z / 2);

    //     const lines = Mesh.CreateLines(KBMAXPREFIX + "boundingBox", [
    //         v1, v2,
    //         v1, v3,
    //         v3, v4,
    //         v4, v2,
    //         v2, v6,
    //         v6, v5,
    //         v5, v7,
    //         v7, v8,
    //         v8, v6,
    //         v8, v4,
    //         v3, v7,
    //         v5, v1
    //     ], this.gizmoLayer.utilityLayerScene);
    //     lines.color = new Color3(1, 1, 1);
    //     lines.isPickable = false;

    //     if (oMesh.kbBoundingBox) {
    //         // the bounding box exists so it must be recalculated and repositioned
    //         bbNode = oMesh.kbBoundingBox;
    //         bbNode.wireFrame.dispose(); // it is easier to recreate it then reposition it
    //         bbNode.wireFrame = lines;
    //     }
    //     else {
    //         // the bounding box does not exist, so it must be created
    //         bbNode = new boundingBoxGeometry(lines);
    //     }

    //     oMesh.kbBoundingBox = bbNode; // force to attach the current boundingBox
    // }

    private filterToChangedValues(vector: Vector3 | Quaternion): Partial<KbVector & KbQuaternion> {
        return {
            x: vector.x ? vector.x : undefined,
            y: vector.y ? vector.y : undefined,
            z: vector.z ? vector.z : undefined,
            w: 'w' in vector ? vector.w : undefined,
        };
    }
}

// class boundingBoxGeometry {
//     mateHelper1?: Mesh;
//     mateHelper2?: Mesh;
//     mateHelper3?: Mesh;
//     mateHelper4?: Mesh;
//     mateHelper5?: Mesh;
//     mateHelper6?: Mesh;
//     wireFrame: Mesh;

//     constructor(wireFrame: Mesh) {
//         this.wireFrame = wireFrame;
//     }
// }
